import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  signal,
  computed,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  merge,
  map as rxMap,
  of,
  retry,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { LANGUAGES } from '@app/core/constants/language.constants';
import { LanguageService } from '@services/language.service';
import { MemberService } from '@services/member.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '@shared/confirm-dialog/confirm-dialog.component';
import { GuidedCourseCreationService } from '@services/guided-course-creation.service';
import { GtmWrapperService } from '@services/gtm-wrapper.service';
import { Member } from '@models/member.model';
import { ElearnioLanguageSpanModule } from '@shared/elearnio-language-icon/elearnio-language-span.module';
import { GraphicOptionCardComponent } from '@shared/graphic-option-card/graphic-option-card.component';
import { TooltipButtonComponent } from '@app/core/components/tooltip-button/tooltip-button.component';
import { type2img } from '@helper/h5p-icons-helper';

import {
  COURSE_INTENT_OPTIONS,
  TARGET_AUDIENCE_OPTIONS,
  getModuleTypeConfig,
  mapOutlineApiResponse,
  CourseIntent,
  TargetAudience,
  OptionConfig,
  CourseOutline,
  CourseSectionOutline,
  OutlineApiResponse,
  SkeletonRequestPayload,
  SkeletonResponse,
  SectionContentParagraph,
  getAiErrorTranslationKey,
  AI_ERROR_CODES,
  AiErrorCode,
  isAiContentSupported,
} from '@core/constants/guided-course-creation.constants';
import { CourseCreationErrorDialogComponent } from '../course-creation-error-dialog/course-creation-error-dialog.component';
import { HourglassSpinnerComponent } from '@shared/hourglass-spinner/hourglass-spinner.component';
import { OutlineChatComponent } from './outline-chat/outline-chat.component';

type DialogPage = 'form' | 'outline';

@Component({
  selector: 'app-guided-course-creation-dialog',
  templateUrl: './guided-course-creation-dialog.component.html',
  styleUrls: ['./guided-course-creation-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatExpansionModule,
    MatSelectModule,
    MatFormFieldModule,
    MatRadioModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    TranslateModule,
    TooltipButtonComponent,
    ElearnioLanguageSpanModule,
    GraphicOptionCardComponent,
    HourglassSpinnerComponent,
    OutlineChatComponent,
  ],
})
export class GuidedCourseCreationDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<GuidedCourseCreationDialogComponent>,
  );
  private readonly fb = inject(FormBuilder);
  private readonly languageService = inject(LanguageService);
  private readonly memberService = inject(MemberService);
  private readonly translateService = inject(TranslateService);
  private readonly matDialog = inject(MatDialog);
  private readonly guidedCourseCreationService = inject(
    GuidedCourseCreationService,
  );
  private readonly gtmService = inject(GtmWrapperService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly intentOptions: readonly OptionConfig<CourseIntent>[] =
    COURSE_INTENT_OPTIONS;
  readonly audienceOptions: readonly OptionConfig<TargetAudience>[] =
    TARGET_AUDIENCE_OPTIONS;
  readonly availableLanguages = Object.values(LANGUAGES).filter(
    lang => lang !== LANGUAGES.ZZ,
  );
  @ViewChild('scrollContainer')
  private scrollContainer!: ElementRef<HTMLElement>;

  readonly sessionId = signal<string | null>(null);
  readonly isSessionLoading = signal(true);
  readonly currentPage = signal<DialogPage>('form');
  readonly outline = signal<CourseOutline | null>(null);
  readonly skeletonResponse = signal<SkeletonResponse | null>(null);
  readonly isLoading = signal(false);
  readonly isSectionsLoading = signal(false);
  readonly sectionsError = signal(false);
  private readonly failedSectionIndices = signal<number[]>([]);
  readonly contentGenerationEnabled = signal(true);
  readonly isAiContentSupported = isAiContentSupported;
  readonly learningObjectives = computed(() => {
    const objectives = this.outline()?.learningObjectives ?? [];
    const joined = objectives.join('. ');
    return joined ? joined + '.' : '';
  });
  private sessionSubscription: Subscription | null = null;
  private generateSubscription: Subscription | null = null;
  private createCourseSubscription: Subscription | null = null;

  readonly member = signal<Member | undefined>(undefined);
  readonly termsAlreadyAccepted = signal(false);

  readonly form = this.fb.nonNullable.group({
    topic: ['', Validators.required],
    language: [
      this.languageService.getCurrentLanguage() as LANGUAGES,
      Validators.required,
    ],
    intent: ['' as CourseIntent | '', Validators.required],
    audience: [
      [] as TargetAudience[],
      [Validators.required, Validators.minLength(1)],
    ],
  });

  readonly formSummary = computed(() => {
    const raw = this.form.getRawValue();
    const intentOption = this.intentOptions.find(o => o.id === raw.intent);
    const audienceLabels = raw.audience
      .map(a => {
        const option = this.audienceOptions.find(o => o.id === a);
        return option ? this.translateService.instant(option.titleKey) : a;
      })
      .join(', ');

    return {
      intentLabel: intentOption
        ? this.translateService.instant(intentOption.titleKey)
        : '',
      audienceLabel: audienceLabels,
    };
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.sessionSubscription?.unsubscribe();
      this.generateSubscription?.unsubscribe();
      this.createCourseSubscription?.unsubscribe();
    });

    this.memberService
      .current()
      .pipe(take(1))
      .subscribe(member => {
        if (!member) return;
        this.member.set(member);
        this.termsAlreadyAccepted.set(member.aiTermsAgreedTo);
      });

    this.createSession();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (
      this.currentPage() === 'outline' ||
      this.isLoading() ||
      this.isSectionsLoading()
    ) {
      event.preventDefault();
    }
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  isAudienceSelected(id: TargetAudience): boolean {
    return this.form.controls.audience.value.includes(id);
  }

  toggleAudience(id: TargetAudience): void {
    const current = this.form.controls.audience.value;
    const next = current.includes(id)
      ? current.filter(a => a !== id)
      : [...current, id];
    this.form.controls.audience.setValue(next);
  }

  onGenerate(): void {
    const sessionId = this.sessionId();
    if (!this.form.valid || !this.member()?.aiTermsAgreedTo || !sessionId)
      return;

    const raw = this.form.getRawValue();
    if (!raw.intent) return;

    this.gtmService.pushTag({ event: 'guided_course_outline_generated' });
    this.isLoading.set(true);

    const payload: SkeletonRequestPayload = {
      topic: raw.topic,
      language: raw.language,
      intent: raw.intent,
      audience: raw.audience,
    };

    this.generateSubscription = this.guidedCourseCreationService
      .generateSkeleton(sessionId, payload)
      .pipe(
        take(1),
        tap(response => {
          this.skeletonResponse.set(response);
          this.setSummary(response);
          this.isLoading.set(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.showSkeletonErrorDialog(this.getAiErrorCode(err));
          return EMPTY;
        }),
        switchMap(skeleton => {
          this.sectionsError.set(false);
          this.failedSectionIndices.set([]);
          this.isSectionsLoading.set(true);
          const sections = skeleton.sections.map((s, index) => ({
            sectionId: s.section_id,
            index,
          }));

          return this.fetchSectionContents(sessionId, sections);
        }),
      )
      .subscribe(this.sectionContentObserver());
  }

  private retrySections(): void {
    this.generateSubscription?.unsubscribe();

    const sessionId = this.sessionId();
    const skeleton = this.skeletonResponse();
    if (!skeleton || !sessionId) return;

    const failedIndices = this.failedSectionIndices();
    if (!failedIndices.length) return;

    this.sectionsError.set(false);
    this.failedSectionIndices.set([]);
    this.isSectionsLoading.set(true);
    const sections = failedIndices.map(index => ({
      sectionId: skeleton.sections[index].section_id,
      index,
    }));

    this.generateSubscription = this.fetchSectionContents(
      sessionId,
      sections,
      0,
    ).subscribe(this.sectionContentObserver());
  }

  private sectionContentObserver() {
    return {
      next: (result: {
        index: number;
        paragraphs: SectionContentParagraph[];
        error: boolean;
      }) => {
        this.handleSectionResult(result.index, result.paragraphs, result.error);
      },
      complete: () => {
        this.onSectionFetchComplete();
      },
    };
  }

  private handleSectionResult(
    index: number,
    paragraphs: SectionContentParagraph[],
    error: boolean,
  ): void {
    if (error) {
      this.failedSectionIndices.update(ids => [...ids, index]);
    } else {
      this.setSectionAt(index, paragraphs);
    }
  }

  private onSectionFetchComplete(): void {
    if (this.failedSectionIndices().length > 0) {
      this.isSectionsLoading.set(false);
      this.sectionsError.set(true);
      this.showSectionsErrorDialog(AI_ERROR_CODES.GENERIC);
    } else {
      this.isSectionsLoading.set(false);
    }
  }

  private fetchSectionContents(
    sessionId: string,
    sections: { sectionId: number; index: number }[],
    retryCount = 3,
  ) {
    return merge(
      ...sections.map(({ sectionId, index }) =>
        this.guidedCourseCreationService
          .generateSectionContent(sessionId, sectionId)
          .pipe(
            take(1),
            retry(retryCount),
            rxMap(paragraphs => ({ index, paragraphs, error: false })),
            catchError(() =>
              of({
                index,
                paragraphs: [] as SectionContentParagraph[],
                error: true,
              }),
            ),
          ),
      ),
    );
  }

  private setSummary(response: SkeletonResponse): void {
    this.outline.set({
      title: response.course_title,
      totalTimeMinutes: response.estimated_duration_minutes,
      learningObjectives: response.learning_objectives ?? [],
      sections: response.sections.map(s => ({
        headline: s.section_title,
        modules: [],
      })),
    });
    this.currentPage.set('outline');
    this.scrollContainer?.nativeElement?.scrollTo(0, 0);
  }

  private setSectionAt(
    index: number,
    paragraphs: SectionContentParagraph[],
  ): void {
    const current = this.outline();
    if (!current) return;

    const section: CourseSectionOutline = {
      headline: current.sections[index]?.headline ?? '',
      modules: paragraphs.map(p => {
        const config = getModuleTypeConfig(p.suggested_module);
        return {
          name: p.module_headline,
          moduleType: config?.displayKey ?? p.suggested_module,
          rawModuleType: p.suggested_module,
          estimatedTimeMinutes: p.estimated_duration_minutes,
          whatToCover: p.what_to_cover,
        };
      }),
    };

    const sections = [...current.sections];
    sections[index] = section;
    this.outline.set(this.enrichOutlineIcons({ ...current, sections }));
  }

  onBack(): void {
    this.showUnsavedChangesConfirm()
      .pipe(take(1))
      .subscribe(confirmed => {
        if (confirmed) {
          this.generateSubscription?.unsubscribe();
          this.currentPage.set('form');
          this.outline.set(null);
          this.skeletonResponse.set(null);
          this.isLoading.set(false);
          this.isSectionsLoading.set(false);
          this.sectionsError.set(false);
          this.failedSectionIndices.set([]);
          this.contentGenerationEnabled.set(true);
          this.createSession();
        }
      });
  }

  onOutlineUpdated(outline: OutlineApiResponse): void {
    this.outline.set(this.enrichOutlineIcons(mapOutlineApiResponse(outline)));
  }

  onCreateCourse(): void {
    if (!this.form.valid) return;
    const raw = this.form.getRawValue();
    const currentOutline = this.outline();
    const member = this.member();
    if (!currentOutline || !member) return;

    if (member.hasDirtyAttributes) {
      member.save().pipe(take(1)).subscribe();
    }

    this.gtmService.pushTag({ event: 'guided_course_created' });

    this.createCourseSubscription = this.guidedCourseCreationService
      .create({
        title: currentOutline.title,
        language: raw.language,
        estimatedTimeInMinutes: currentOutline.totalTimeMinutes,
        outline: currentOutline,
        author: member,
      })
      .subscribe({
        next: course => {
          this.dialogRef.close();
          this.router.navigate(['/courses', course.id, 'content'], {
            queryParams: { fromGcc: true },
          });
        },
        error: (err: unknown) => {
          this.showCreationErrorDialog(err);
        },
      });
  }

  onClose(): void {
    if (this.currentPage() === 'outline') {
      this.showUnsavedChangesConfirm()
        .pipe(take(1))
        .subscribe(confirmed => {
          if (confirmed) {
            this.dialogRef.close();
          }
        });
    } else {
      this.dialogRef.close();
    }
  }

  private showUnsavedChangesConfirm(): Observable<boolean> {
    const data: ConfirmDialogData = {
      message: this.translateService.instant(
        'common.confirmation.unsaved_changes',
      ),
      title: this.translateService.instant('common.confirmation.cancel_title'),
    };
    return this.matDialog
      .open(ConfirmDialogComponent, {
        data,
        panelClass: 'elearnio-mat-dialog-panel-class',
        scrollStrategy: new NoopScrollStrategy(),
      })
      .afterClosed();
  }

  onTermsAndConditionsChange(event: MatCheckboxChange): void {
    const member = this.member();
    if (member) {
      member.aiTermsAgreedTo = event.checked;
      if (member.hasDirtyAttributes) {
        member.save().pipe(take(1)).subscribe();
      }
    }
  }

  private showCreationErrorDialog(err: unknown): void {
    this.showErrorDialogWithRetry(this.extractErrorMessage(err), () =>
      this.onCreateCourse(),
    );
  }

  private extractErrorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (
      typeof err === 'object' &&
      err !== null &&
      'message' in err &&
      typeof (err as Record<string, unknown>)['message'] === 'string'
    ) {
      return (err as Record<string, unknown>)['message'] as string;
    }
    return this.translateService.instant(
      'guidedCourseCreation.errorDialog.unknownError',
    );
  }

  private showSkeletonErrorDialog(errorCode: AiErrorCode): void {
    this.showErrorDialogWithRetry(
      this.translateService.instant(getAiErrorTranslationKey(errorCode)),
      () => this.onGenerate(),
    );
  }

  private showSectionsErrorDialog(errorCode: AiErrorCode): void {
    this.showErrorDialogWithRetry(
      this.translateService.instant(getAiErrorTranslationKey(errorCode)),
      () => this.retrySections(),
    );
  }

  private getAiErrorCode(err: HttpErrorResponse): AiErrorCode {
    return err.status === 408 || err.status === 504
      ? AI_ERROR_CODES.TIMEOUT
      : AI_ERROR_CODES.GENERIC;
  }

  private showErrorDialogWithRetry(
    errorMessage: string,
    retryFn: () => void,
    dismissFn?: () => void,
  ): void {
    const ref = this.matDialog.open(CourseCreationErrorDialogComponent, {
      data: { errorMessage },
      panelClass: 'elearnio-mat-dialog-panel-class',
      autoFocus: false,
    });

    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((retry: boolean) => {
        if (retry) {
          retryFn();
        } else {
          dismissFn?.();
        }
      });
  }

  private createSession(): void {
    this.isSessionLoading.set(true);
    this.sessionSubscription = this.guidedCourseCreationService
      .createSession()
      .pipe(take(1))
      .subscribe({
        next: id => {
          this.sessionId.set(id);
          this.isSessionLoading.set(false);
        },
        error: () => {
          this.isSessionLoading.set(false);
          this.showErrorDialogWithRetry(
            this.translateService.instant(
              getAiErrorTranslationKey(AI_ERROR_CODES.GENERIC),
            ),
            () => this.createSession(),
            () => this.dialogRef.close(),
          );
        },
      });
  }

  private enrichOutlineIcons(outline: CourseOutline): CourseOutline {
    return {
      ...outline,
      sections: outline.sections.map(section => ({
        ...section,
        modules: section.modules.map(mod => {
          const config = getModuleTypeConfig(mod.rawModuleType);
          return {
            ...mod,
            iconPath: type2img(config?.iconType ?? mod.moduleType),
          };
        }),
      })),
    };
  }
}
